import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import Button from '../buttons/Button';
import {
  CreateBankTransactionModel,
  ContributionPlanWithPayments,
} from '../../../types/bankAccount';
import { Connection } from '../../../redux/slices/connectionsSlice';
import * as bankAccountService from '../../../services/community/bankAccountService';
import { formatEuroCents } from '../../../utils/bankAccountUtil';

type ContributionPlanMatch = {
  transaction: CreateBankTransactionModel;
  matchedConnection: Connection;
  contributionPlans: ContributionPlanWithPayments[];
  selectedPlanId?: number;
};

type VerifyContributionPlanMatchesProps = {
  matches: {
    transaction: CreateBankTransactionModel;
    matchedConnection: Connection;
  }[];
  onConfirm: (
    confirmed: {
      transaction: CreateBankTransactionModel;
      matchedConnection: Connection;
      contributionPlanId?: number;
    }[],
  ) => void;
};

export default function VerifyContributionPlanMatchesView({
  matches,
  onConfirm,
}: VerifyContributionPlanMatchesProps) {
  const { t } = useTranslation();
  const [contributionPlanMatches, setContributionPlanMatches] = useState<ContributionPlanMatch[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContributionPlans = async () => {
      try {
        // Get all contribution plans
        const allPlans = await bankAccountService.getAllContributionPlans();
        // Get detailed payment information for each plan
        const plansWithPayments = await Promise.all(
          allPlans.map((plan) =>
            bankAccountService.getContributionPlanWithPayments(plan.contributionPlanId),
          ),
        );
        // Filter plans that apply to the matched connections and are not fully paid
        const matchesWithPlans: ContributionPlanMatch[] = matches.map((match) => {
          const applicablePlans = plansWithPayments.filter((plan) => {
            // Check if the plan applies to this connection
            const appliesToConnection = plan.appliesTo.some(
              (member) => member.dashboardMemberId === +match.matchedConnection.dashboardMemberId,
            );

            if (!appliesToConnection) return false;

            // Check if the plan is not fully paid
            const getPaymentAmount = (payment: any): number => {
              // Use only journal entry - transactions have been phased out
              if (payment.journalEntry?.lines) {
                // Calculate net amount from journal entry lines
                const netAmount = payment.journalEntry.lines.reduce(
                  (sum: number, line: any) => sum + line.debit - line.credit,
                  0,
                );
                return Math.abs(netAmount);
              }
              return 0;
            };
            const totalPaid = plan.contributionPayments
              .filter((payment) => payment.dashboardMemberId === +match.matchedConnection.dashboardMemberId)
              .reduce((sum, payment) => sum + getPaymentAmount(payment), 0);

            const isNotFullyPaid = totalPaid < plan.amount;

            return isNotFullyPaid;
          });

          return {
            transaction: match.transaction,
            matchedConnection: match.matchedConnection,
            contributionPlans: applicablePlans,
          };
        });

        setContributionPlanMatches(matchesWithPlans);
      } catch (error) {
        console.error('Error fetching contribution plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContributionPlans();
  }, [matches]);

  const selectPlan = (matchIndex: number, planId: number) => {
    setContributionPlanMatches((prev) =>
      prev.map((match, index) =>
        index === matchIndex ? { ...match, selectedPlanId: planId } : match,
      ),
    );
  };

  const handleConfirm = () => {
    const confirmed = contributionPlanMatches.map((match) => ({
      transaction: match.transaction,
      matchedConnection: match.matchedConnection,
      contributionPlanId: match.selectedPlanId,
    }));

    onConfirm(confirmed);
  };

  const hasMatchesWithPlans = contributionPlanMatches.some(
    (match) => match.contributionPlans.length > 0,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="border-primary-600 mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2" />
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!hasMatchesWithPlans) {
    return (
      <div className="py-8 text-center">
        <p className="mb-4 text-gray-600">
          {t('component.modal.uploadFinancialFile.noContributionPlansFound')}
        </p>
        <Button variant="primary-dark" onClick={handleConfirm}>
          {t('continue')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <p className="text-primary-800 mb-6 text-sm">
        {t('component.modal.uploadFinancialFile.contributionPlanMatchDescription')}
      </p>

      <ul className="mb-6 space-y-4">
        {contributionPlanMatches
          .filter((match) => match.contributionPlans.length > 0)
          .map((match, index) => (
            <li
              key={`${index}_contribution_match_${match.transaction.description}`}
              className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="mb-3">
                <div className="font-medium text-gray-900">{match.transaction.description}</div>
                <div className="text-sm text-gray-600">
                  {formatEuroCents(match.transaction.amount, 'en')} →{' '}
                  {match.matchedConnection.fields.FIRST_NAME}{' '}
                  {match.matchedConnection.fields.LAST_NAME}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  {t('component.modal.uploadFinancialFile.selectContributionPlan')}:
                </label>
                <div className="space-y-2">
                  {match.contributionPlans.map((plan) => {
                    const getPaymentAmount = (payment: any): number => {
                      if (payment.transaction?.amount) {
                        return payment.transaction.amount;
                      }
                      if (payment.journalEntry?.lines) {
                        const debitLine = payment.journalEntry.lines.find((line: any) => line.debit > 0);
                        if (debitLine) {
                          return debitLine.debit;
                        }
                        return payment.journalEntry.lines.reduce((sum: number, line: any) => sum + line.debit, 0);
                      }
                      return 0;
                    };
                    const totalPaid = plan.contributionPayments
                      .filter((payment) => payment.dashboardMemberId === +match.matchedConnection.dashboardMemberId)
                      .reduce((sum, payment) => sum + getPaymentAmount(payment), 0);
                    const remainingAmount = plan.amount - totalPaid;

                    return (
                      <label
                        key={plan.contributionPlanId}
                        className="flex cursor-pointer items-center space-x-3">
                        <input
                          type="radio"
                          name={`plan_${index}`}
                          value={plan.contributionPlanId}
                          checked={match.selectedPlanId === plan.contributionPlanId}
                          onChange={() => selectPlan(index, plan.contributionPlanId)}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{plan.name}</div>
                          <div className="text-sm text-gray-600">
                            {t('component.modal.uploadFinancialFile.remainingAmount', {
                              amount: formatEuroCents(remainingAmount, 'en'),
                            })}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </li>
          ))}
      </ul>

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={handleConfirm}>
          {t('skip')}
        </Button>
        <Button variant="primary-dark" onClick={handleConfirm}>
          {t('continue')}
        </Button>
      </div>
    </div>
  );
}
